import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';

/* ─── Layout ─────────────────────────────────────────────────────────────── */
const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
`;

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
`;

/* ─── Summary grid ────────────────────────────────────────────────────────── */
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const StatCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  background: #f9fafb;
`;

const StatLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
`;

const StatValue = styled.div`
  margin-top: 4px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
`;

const StatHint = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
`;

/* ─── Progress bar ────────────────────────────────────────────────────────── */
const ProgressCard = styled.div`
  margin-top: 16px;
  border-radius: 16px;
  padding: 16px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const ProgressTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const ProgressFigure = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1d4ed8;
`;

const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #22c55e, #16a34a);
  width: ${(p) => `${Math.min(p.$value || 0, 100)}%`};
  transition: width 0.3s ease;
`;

/* ─── Empty / Error states ────────────────────────────────────────────────── */
const EmptyState = styled.div`
  padding: 32px;
  border: 2px dashed #dbeafe;
  border-radius: 16px;
  text-align: center;
  color: #0f172a;
  background: #eff6ff;
`;

const EmptyTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
`;

const EmptyText = styled.p`
  margin: 8px 0 0;
  color: #475569;
  font-size: 14px;
`;

const ErrorState = styled(EmptyState)`
  border-color: #fee2e2;
  background: #fef2f2;
  color: #991b1b;
`;

/* ─── Event type cards ────────────────────────────────────────────────────── */
const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const TypeCard = styled.div`
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${(p) => p.$color || '#6366f1'};
  background: #ffffff;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.1s ease, border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
    background: #f9fafb;
  }

  &.is-selected {
    box-shadow: 0 0 0 2px ${(p) => p.$color || '#6366f1'};
    background: ${(p) => (p.$color ? `${p.$color}12` : '#eef2ff')};
  }
`;

const TypeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
`;

const TypeTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const TypePercentage = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1d4ed8;
`;

const TypeMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const BadgeRow = styled.div`
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Badge = styled.span`
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
`;

/* ─── Sessions table ──────────────────────────────────────────────────────── */
const SessionsWrapper = styled.div`
  margin-top: 8px;
`;

const SessionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: #4b5563;
`;

const FilterBar = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterChip = styled.button`
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? '#4f46e5' : '#e5e7eb')};
  background: ${(p) => (p.$active ? '#eef2ff' : '#ffffff')};
  color: ${(p) => (p.$active ? '#312e81' : '#4b5563')};
  font-size: 12px;
  padding: 5px 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #4f46e5;
  }
`;

const SessionTable = styled.div`
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  background: #ffffff;
`;

const SessionRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1.8fr 1fr 1fr 1fr 0.9fr;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  align-items: center;
  border-top: 1px solid #f3f4f6;

  &:first-child {
    border-top: none;
  }
`;

const SessionHeaderRow = styled(SessionRow)`
  background: #f9fafb;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`;

const DateHeading = styled.div`
  margin: 12px 4px 4px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const StatusPill = styled.span`
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.$bg || '#e5e7eb'};
  color: ${(p) => p.$color || '#374151'};
`;

const TypeBadge = styled.span`
  display: inline-block;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.$color ? `${p.$color}22` : '#f3f4f6')};
  color: ${(p) => p.$color || '#374151'};
`;

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (value) => {
  if (!value) return null;
  const [hourRaw, minuteRaw] = String(value).split(':');
  const hourNum = parseInt(hourRaw, 10);
  const minuteNum = parseInt(minuteRaw || '0', 10);
  if (Number.isNaN(hourNum)) return value;
  const period = hourNum >= 12 ? 'PM' : 'AM';
  const hour12 = ((hourNum + 11) % 12) + 1;
  const hourStr = String(hour12).padStart(2, '0');
  const minuteStr = String(Number.isNaN(minuteNum) ? 0 : minuteNum).padStart(2, '0');
  return `${hourStr}:${minuteStr} ${period}`;
};

const formatPercentage = (value) => {
  if (value === null || value === undefined) return '0%';
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${Math.round(num * 10) / 10}%`;
};

const statusPalette = {
  Present: { bg: '#dcfce7', color: '#166534', label: 'Present' },
  Absent:  { bg: '#fee2e2', color: '#b91c1c', label: 'Absent' },
  Late:    { bg: '#fef3c7', color: '#b45309', label: 'Late' },
  Excused: { bg: '#e0f2fe', color: '#0369a1', label: 'Excused' },
};

/* ─── Component ───────────────────────────────────────────────────────────── */
export default function EventAttendance() {
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilterId, setTypeFilterId] = useState(null);

  useEffect(() => {
    const user = AuthService.getUser();
    const registration = user?.selectedRegistration || null;

    if (!registration?.registrationNumber) {
      setError('Please select a course to view event attendance.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    axiosInstance
      .get('/students/me/event-attendance', {
        params: { registrationNumber: registration.registrationNumber },
      })
      .then((resp) => {
        if (cancelled) return;
        setSummary(resp?.data?.summary || null);
        setSessions(Array.isArray(resp?.data?.sessions) ? resp.data.sessions : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load event attendance', err);
        const msg =
          err?.response?.data?.message || "We couldn't load event attendance right now.";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  /* ── Event type stats ── */
  const typeStats = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      const key = s.eventTypeId != null ? String(s.eventTypeId) : '__none__';
      const existing = map.get(key) || {
        id: key,
        label: s.eventTypeName || 'Uncategorised',
        color: s.eventTypeColor || null,
        total: 0,
        present: 0,
        absent: 0,
      };
      existing.total += 1;
      if (s.status === 'Present') existing.present += 1;
      else if (s.status === 'Absent') existing.absent += 1;
      map.set(key, existing);
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [sessions]);

  /* ── Filter options ── */
  const typeFilterOptions = useMemo(() => typeStats, [typeStats]);

  /* ── Filtered sessions ── */
  const filteredSessions = useMemo(() => {
    if (!typeFilterId) return sessions;
    return sessions.filter((s) => {
      const key = s.eventTypeId != null ? String(s.eventTypeId) : '__none__';
      return key === typeFilterId;
    });
  }, [sessions, typeFilterId]);

  /* ── Grouped by date ── */
  const groupedSessions = useMemo(() => {
    if (!filteredSessions.length) return [];
    const groups = new Map();
    filteredSessions.forEach((s) => {
      const key = s.date || '';
      const label = formatDate(s.date);
      if (!groups.has(key)) groups.set(key, { key, label, items: [] });
      groups.get(key).items.push(s);
    });
    return Array.from(groups.values());
  }, [filteredSessions]);

  const title = 'Event Attendance';

  return (
    <StudentLayout title={title}>
      <Wrapper>
        {/* ── Summary ── */}
        <Section>
          <SectionHeading>
            <Title>Event attendance summary</Title>
            <Subtitle>
              Your attendance record for other events (workshops, seminars, orientation sessions, etc.) assigned to your batch.
            </Subtitle>
          </SectionHeading>

          {loading && <Loader message="Loading event attendance…" />}

          {!loading && error && (
            <ErrorState>
              <EmptyTitle>Unable to load event attendance</EmptyTitle>
              <EmptyText>{error}</EmptyText>
            </ErrorState>
          )}

          {!loading && !error && summary && summary.total > 0 && (
            <>
              <SummaryGrid>
                <StatCard>
                  <StatLabel>Overall percentage</StatLabel>
                  <StatValue>{formatPercentage(summary.attendancePercentage)}</StatValue>
                  <StatHint>Events attended out of total</StatHint>
                </StatCard>
                <StatCard>
                  <StatLabel>Events attended</StatLabel>
                  <StatValue>{summary.present}</StatValue>
                  <StatHint>Out of {summary.total} recorded events</StatHint>
                </StatCard>
                <StatCard>
                  <StatLabel>Events absent</StatLabel>
                  <StatValue>{summary.absent}</StatValue>
                  <StatHint>Absences across all event types</StatHint>
                </StatCard>
                <StatCard>
                  <StatLabel>Total events</StatLabel>
                  <StatValue>{summary.total}</StatValue>
                  <StatHint>All recorded event attendance entries</StatHint>
                </StatCard>
              </SummaryGrid>

              <ProgressCard>
                <ProgressHeader>
                  <ProgressTitle>Event attendance coverage</ProgressTitle>
                  <ProgressFigure>{formatPercentage(summary.attendancePercentage)}</ProgressFigure>
                </ProgressHeader>
                <ProgressTrack>
                  <ProgressFill $value={summary.attendancePercentage} />
                </ProgressTrack>
              </ProgressCard>
            </>
          )}

          {!loading && !error && (!summary || summary.total === 0) && (
            <EmptyState>
              <EmptyTitle>No event attendance records found</EmptyTitle>
              <EmptyText>
                Once lecturers mark attendance for special events assigned to your batch, your records will appear here.
              </EmptyText>
            </EmptyState>
          )}
        </Section>

        {/* ── All sessions table ── */}
        {!loading && !error && sessions.length > 0 && (
          <Section>
            <SectionHeading>
              <Title>All event attendance entries</Title>
              <Subtitle>
                Detailed list of every event attendance entry recorded for your batch, grouped by date.
              </Subtitle>
            </SectionHeading>

            <SessionsWrapper>
              <SessionsHeader>
                <span>{filteredSessions.length} record(s) found</span>
              </SessionsHeader>

              <FilterBar>
                <FilterChip
                  type="button"
                  $active={!typeFilterId}
                  onClick={() => setTypeFilterId(null)}
                >
                  <span>All types</span>
                  <span>· {sessions.length}</span>
                </FilterChip>
                {typeFilterOptions.map((opt) => (
                  <FilterChip
                    key={opt.id}
                    type="button"
                    $active={typeFilterId === opt.id}
                    onClick={() => setTypeFilterId(typeFilterId === opt.id ? null : opt.id)}
                  >
                    <span>{opt.label}</span>
                    <span>· {opt.total}</span>
                  </FilterChip>
                ))}
              </FilterBar>

              {groupedSessions.map((group) => (
                <div key={group.key}>
                  <DateHeading>{group.label}</DateHeading>
                  <SessionTable>
                    <SessionHeaderRow>
                      <div>Date</div>
                      <div>Event</div>
                      <div>Type</div>
                      <div>Hall / Room</div>
                      <div>Time</div>
                      <div>Status</div>
                    </SessionHeaderRow>
                    {group.items.map((session) => {
                      const palette = statusPalette[session.status] || statusPalette.Absent;
                      const timeLabel = [formatTime(session.startTime), formatTime(session.endTime)]
                        .filter(Boolean)
                        .join(' – ');
                      return (
                        <SessionRow key={session.attendanceId}>
                          <div>{formatDate(session.date)}</div>
                          <div>{session.eventTitle || '—'}</div>
                          <div>
                            {session.eventTypeName
                              ? <TypeBadge $color={session.eventTypeColor}>{session.eventTypeName}</TypeBadge>
                              : '—'}
                          </div>
                          <div>{session.roomName || '—'}</div>
                          <div>{timeLabel || '—'}</div>
                          <div>
                            <StatusPill $bg={palette.bg} $color={palette.color}>
                              {palette.label}
                            </StatusPill>
                          </div>
                        </SessionRow>
                      );
                    })}
                  </SessionTable>
                </div>
              ))}
            </SessionsWrapper>
          </Section>
        )}
      </Wrapper>
    </StudentLayout>
  );
}
